import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import {
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  Chip,
  Button
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PaymentsIcon from '@mui/icons-material/Payments';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import DiamondIcon from '@mui/icons-material/Diamond';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { DataContext } from '../../context/DataContext';

const GirviLedger = () => {
  const { id } = useParams();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { girvi, girviPayments, loading } = useContext(DataContext);
  
  const [currentGirvi, setCurrentGirvi] = useState(null);
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState({
    principal: 0,
    totalReceived: 0,
    pending: 0
  });

  useEffect(() => {
    if (id) {
      const girviData = girvi?.find(g => g.id === id);
      const paymentData = girviPayments?.filter(p => p.girviId === id);
      
      if (girviData) {
        setCurrentGirvi(girviData);
        
        let totalReceived = 0;
        let lastRemaining = parseFloat(girviData.initialPrincipal);
        
        paymentData?.forEach(payment => {
          totalReceived += parseFloat(payment.paymentAmount || 0);
          lastRemaining = parseFloat(payment.remainingPrincipal || 0);
        });

        setPayments(paymentData);
        setStats({
          principal: parseFloat(girviData.initialPrincipal),
          totalReceived,
          pending: lastRemaining
        });
      }
    }
  }, [id, girvi, girviPayments]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center',
        py: 4,
        px: { xs: 2, sm: 3, md: 4 },
        background: isDark
          ? "linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)"
          : "linear-gradient(135deg, rgba(224, 209, 209, 0.95) 0%, rgba(255, 255, 255, 0.95) 100%)",
      }}>
        <CircularProgress 
          size={60}
          thickness={3}
          sx={{ 
            color: isDark ? '#3b82f6' : '#10b981',
            mb: 3
          }} 
        />
        <Typography 
          variant="body1" 
          sx={{ 
            color: 'text.secondary',
            fontWeight: 500
          }}
        >
          Loading Ledger Details...
        </Typography>
      </Box>
    );
  }

  if (!girvi) {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        py: 4,
        px: { xs: 2, sm: 3, md: 4 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isDark
          ? "linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)"
          : "linear-gradient(135deg, rgba(224, 209, 209, 0.95) 0%, rgba(255, 255, 255, 0.95) 100%)",
      }}>
        <Alert 
          severity="error" 
          sx={{ 
            maxWidth: '600px',
            borderRadius: '16px',
            backgroundColor: isDark 
              ? 'rgba(30, 41, 59, 0.7)'
              : 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            color: theme.palette.error.main,
            '& .MuiAlert-icon': {
              color: theme.palette.error.main
            }
          }}
        >
          No details found for this Girvi ID.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      py: 4,
      px: { xs: 2, sm: 3, md: 3 },
    }}>
      <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
        {/* Back Button */}
        {/* <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            mb: 3,
            borderRadius: '12px',
            color: 'text.primary',
            borderColor: isDark 
              ? 'rgba(148, 163, 184, 0.1)' 
              : 'rgba(226, 232, 240, 0.6)',
            backgroundColor: isDark 
              ? 'rgba(30, 41, 59, 0.3)' 
              : 'rgba(255, 255, 255, 0.3)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: isDark 
                ? 'rgba(30, 41, 59, 0.5)' 
                : 'rgba(255, 255, 255, 0.5)',
              transform: 'translateX(-4px)',
            }
          }}
          variant="outlined"
        >
          Back to Dashboard
        </Button> */}

        {/* Main Ledger Card */}
        <Paper 
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            backgroundColor: isDark 
              ? 'rgba(30, 41, 59, 0.4)'
              : 'rgba(255, 255, 255, 0.4)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `1px solid ${isDark 
              ? 'rgba(148, 163, 184, 0.1)'
              : 'rgba(255, 255, 255, 0.2)'
            }`,
            borderRadius: '24px',
            boxShadow: isDark 
              ? '0 8px 32px rgba(0, 0, 0, 0.2)'
              : '0 8px 32px rgba(31, 38, 135, 0.1)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: isDark 
                ? '0 12px 40px rgba(0, 0, 0, 0.3)'
                : '0 12px 40px rgba(31, 38, 135, 0.15)',
            }
          }}
        >
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{ 
                width: 56,
                height: 56,
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: isDark
                  ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                  : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                boxShadow: '0 4px 20px rgba(139, 92, 246, 0.3)'
              }}>
                <ReceiptLongIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography 
                  variant="h4" 
                  sx={{
                    fontWeight: 800,
                    background: isDark
                      ? 'linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%)'
                      : 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    fontSize: { xs: '1.8rem', sm: '2.2rem' }
                  }}
                >
                  Girvi Ledger
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: '1.1rem'
                  }}
                >
                  Complete payment history and transaction details
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Girvi Information Card */}
          <Paper 
            elevation={0}
            sx={{
              p: 3,
              mb: 4,
              backgroundColor: isDark 
                ? 'rgba(30, 41, 59, 0.3)'
                : 'rgba(255, 255, 255, 0.3)',
              backdropFilter: 'blur(20px)',
              border: `1px solid ${isDark 
                ? 'rgba(148, 163, 184, 0.1)'
                : 'rgba(255, 255, 255, 0.2)'
              }`,
              borderRadius: '16px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box sx={{ 
                width: 44,
                height: 44,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isDark 
                  ? alpha('#3b82f6', 0.1)
                  : alpha('#10b981', 0.1),
                color: isDark ? '#3b82f6' : '#10b981'
              }}>
                <PersonIcon />
              </Box>
              <Box>
                <Typography 
                  variant="subtitle1" 
                  fontWeight="bold"
                  sx={{ 
                    color: 'text.primary',
                    fontSize: '1.2rem'
                  }}
                >
                  Girvi Information
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Customer and item details
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  p: 2,
                  backgroundColor: isDark 
                    ? 'rgba(30, 41, 59, 0.2)'
                    : 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${isDark 
                    ? 'rgba(148, 163, 184, 0.05)'
                    : 'rgba(226, 232, 240, 0.3)'
                  }`,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}>
                  <Box sx={{ 
                    minWidth: 36,
                    height: 36,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isDark 
                      ? alpha('#3b82f6', 0.1)
                      : alpha('#10b981', 0.1),
                    color: isDark ? '#3b82f6' : '#10b981'
                  }}>
                    <PersonIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ 
                      color: 'text.secondary',
                      display: 'block'
                    }}>
                      Customer Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {currentGirvi?.customerName}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  p: 2,
                  backgroundColor: isDark 
                    ? 'rgba(30, 41, 59, 0.2)'
                    : 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${isDark 
                    ? 'rgba(148, 163, 184, 0.05)'
                    : 'rgba(226, 232, 240, 0.3)'
                  }`,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}>
                  <Box sx={{ 
                    minWidth: 36,
                    height: 36,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isDark 
                      ? alpha('#8b5cf6', 0.1)
                      : alpha('#8b5cf6', 0.1),
                    color: '#8b5cf6'
                  }}>
                    <PhoneIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ 
                      color: 'text.secondary',
                      display: 'block'
                    }}>
                      Mobile Number
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {currentGirvi?.mobileNo}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  p: 2,
                  backgroundColor: isDark 
                    ? 'rgba(30, 41, 59, 0.2)'
                    : 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${isDark 
                    ? 'rgba(148, 163, 184, 0.05)'
                    : 'rgba(226, 232, 240, 0.3)'
                  }`,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}>
                  <Box sx={{ 
                    minWidth: 36,
                    height: 36,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isDark 
                      ? alpha('#f59e0b', 0.1)
                      : alpha('#f59e0b', 0.1),
                    color: '#f59e0b'
                  }}>
                    <DiamondIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ 
                      color: 'text.secondary',
                      display: 'block'
                    }}>
                      Item & Weight
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {currentGirvi?.itemName} ({currentGirvi?.itemWeight}g)
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  p: 2,
                  backgroundColor: isDark 
                    ? 'rgba(30, 41, 59, 0.2)'
                    : 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${isDark 
                    ? 'rgba(148, 163, 184, 0.05)'
                    : 'rgba(226, 232, 240, 0.3)'
                  }`,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}>
                  <Box sx={{ 
                    minWidth: 36,
                    height: 36,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isDark 
                      ? alpha('#10b981', 0.1)
                      : alpha('#10b981', 0.1),
                    color: '#10b981'
                  }}>
                    <TrendingUpIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ 
                      color: 'text.secondary',
                      display: 'block'
                    }}>
                      Interest Rate
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {currentGirvi?.interestRate}%
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Principal Given */}
            <Grid item xs={12} sm={4}>
              <Card sx={{
                backgroundColor: isDark 
                  ? 'rgba(30, 41, 59, 0.5)'
                  : 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${isDark 
                  ? 'rgba(148, 163, 184, 0.1)'
                  : 'rgba(255, 255, 255, 0.2)'
                }`,
                borderRadius: '16px',
                boxShadow: isDark 
                  ? '0 4px 20px rgba(0, 0, 0, 0.15)'
                  : '0 4px 20px rgba(31, 38, 135, 0.08)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: isDark 
                    ? '0 8px 30px rgba(0, 0, 0, 0.25)'
                    : '0 8px 30px rgba(31, 38, 135, 0.12)',
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box sx={{ 
                      width: 44,
                      height: 44,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: isDark 
                        ? alpha('#3b82f6', 0.1)
                        : alpha('#10b981', 0.1),
                      color: isDark ? '#3b82f6' : '#10b981'
                    }}>
                      <AccountBalanceWalletIcon />
                    </Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary',
                        fontWeight: 600
                      }}
                    >
                      Principal Given
                    </Typography>
                  </Box>
                  <Typography 
                    variant="h4" 
                    fontWeight="bold"
                    sx={{
                      color: isDark ? '#3b82f6' : '#10b981',
                      fontSize: '1.8rem'
                    }}
                  >
                    {formatCurrency(stats.principal)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Total Received */}
            <Grid item xs={12} sm={4}>
              <Card sx={{
                backgroundColor: isDark 
                  ? 'rgba(30, 41, 59, 0.5)'
                  : 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${isDark 
                  ? 'rgba(148, 163, 184, 0.1)'
                  : 'rgba(255, 255, 255, 0.2)'
                }`,
                borderRadius: '16px',
                boxShadow: isDark 
                  ? '0 4px 20px rgba(0, 0, 0, 0.15)'
                  : '0 4px 20px rgba(31, 38, 135, 0.08)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: isDark 
                    ? '0 8px 30px rgba(0, 0, 0, 0.25)'
                    : '0 8px 30px rgba(31, 38, 135, 0.12)',
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box sx={{ 
                      width: 44,
                      height: 44,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: isDark 
                        ? alpha('#10b981', 0.1)
                        : alpha('#10b981', 0.1),
                      color: '#10b981'
                    }}>
                      <PaymentsIcon />
                    </Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary',
                        fontWeight: 600
                      }}
                    >
                      Total Received
                    </Typography>
                  </Box>
                  <Typography 
                    variant="h4" 
                    fontWeight="bold"
                    sx={{
                      color: '#10b981',
                      fontSize: '1.8rem'
                    }}
                  >
                    {formatCurrency(stats.totalReceived)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Pending Amount */}
            <Grid item xs={12} sm={4}>
              <Card sx={{
                backgroundColor: isDark 
                  ? 'rgba(30, 41, 59, 0.5)'
                  : 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${isDark 
                  ? 'rgba(148, 163, 184, 0.1)'
                  : 'rgba(255, 255, 255, 0.2)'
                }`,
                borderRadius: '16px',
                boxShadow: isDark 
                  ? '0 4px 20px rgba(0, 0, 0, 0.15)'
                  : '0 4px 20px rgba(31, 38, 135, 0.08)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: isDark 
                    ? '0 8px 30px rgba(0, 0, 0, 0.25)'
                    : '0 8px 30px rgba(31, 38, 135, 0.12)',
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box sx={{ 
                      width: 44,
                      height: 44,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: isDark 
                        ? alpha('#ef4444', 0.1)
                        : alpha('#ef4444', 0.1),
                      color: '#ef4444'
                    }}>
                      <PendingActionsIcon />
                    </Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary',
                        fontWeight: 600
                      }}
                    >
                      Pending Amount
                    </Typography>
                  </Box>
                  <Typography 
                    variant="h4" 
                    fontWeight="bold"
                    sx={{
                      color: '#ef4444',
                      fontSize: '1.8rem'
                    }}
                  >
                    {formatCurrency(stats.pending)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Payment History */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    color: 'text.primary',
                    fontSize: '1.3rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5
                  }}
                >
                  Payment History
                  <Box component="span" sx={{ 
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '12px',
                    backgroundColor: isDark 
                      ? alpha('#8b5cf6', 0.15)
                      : alpha('#8b5cf6', 0.15),
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${alpha('#8b5cf6', 0.2)}`,
                    color: '#8b5cf6'
                  }}>
                    {payments?.length || 0} Transactions
                  </Box>
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    mt: 0.5
                  }}
                >
                  Complete transaction history for this girvi
                </Typography>
              </Box>
            </Box>

            <TableContainer sx={{ 
              borderRadius: '16px',
              backgroundColor: isDark 
                ? 'rgba(30, 41, 59, 0.3)'
                : 'rgba(255, 255, 255, 0.3)',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${isDark 
                ? 'rgba(51, 65, 85, 0.3)'
                : 'rgba(226, 232, 240, 0.4)'
              }`,
              overflow: 'hidden',
            }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ 
                    backgroundColor: isDark 
                      ? 'rgba(59, 130, 246, 0.1)'
                      : 'rgba(139, 92, 246, 0.1)',
                    backdropFilter: 'blur(10px)',
                    borderBottom: `2px solid ${alpha('#8b5cf6', 0.3)}`
                  }}>
                    <TableCell sx={{ 
                      fontWeight: 700, 
                      color: 'text.primary',
                      py: 2.5,
                      px: 3,
                      fontSize: '0.95rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Date
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 700, 
                      color: 'text.primary',
                      py: 2.5,
                      px: 3,
                      fontSize: '0.95rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Payment Amount
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 700, 
                      color: 'text.primary',
                      py: 2.5,
                      px: 3,
                      fontSize: '0.95rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Type
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 700, 
                      color: 'text.primary',
                      py: 2.5,
                      px: 3,
                      fontSize: '0.95rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Remaining
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 700, 
                      color: 'text.primary',
                      py: 2.5,
                      px: 3,
                      fontSize: '0.95rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Interest Earned
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments?.length > 0 ? (
                    payments?.map((payment, index) => (
                      <TableRow 
                        key={index}
                        sx={{ 
                          borderBottom: `1px solid ${isDark 
                            ? 'rgba(51, 65, 85, 0.2)'
                            : 'rgba(226, 232, 240, 0.3)'
                          }`,
                          transition: 'all 0.2s ease',
                          backgroundColor: index % 2 === 0 
                            ? isDark 
                              ? 'rgba(30, 41, 59, 0.15)'
                              : 'rgba(255, 255, 255, 0.2)'
                            : 'transparent',
                          '&:hover': {
                            backgroundColor: isDark 
                              ? 'rgba(255, 255, 255, 0.1)'
                              : 'rgba(139, 92, 246, 0.08)',
                            transform: 'translateX(4px)',
                            boxShadow: '0 4px 12px rgba(139, 92, 246, 0.1)',
                          }
                        }}
                      >
                        <TableCell sx={{ 
                          color: 'text.primary', 
                          py: 2.5,
                          px: 3,
                          fontWeight: 500,
                          fontSize: '0.95rem'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CalendarTodayIcon sx={{ 
                              color: isDark ? '#94a3b8' : '#718096',
                              fontSize: 18 
                            }} />
                            {formatDate(payment.date)}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          py: 2.5,
                          px: 3,
                          fontWeight: 600
                        }}>
                          <Typography sx={{ 
                            color: '#10b981',
                            fontWeight: 700
                          }}>
                            {formatCurrency(payment.paymentAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2.5, px: 3 }}>
                          <Chip
                            label={payment.type}
                            size="small"
                            sx={{
                              backgroundColor: payment.type === 'Interest' 
                                ? isDark 
                                  ? alpha('#3b82f6', 0.15)
                                  : alpha('#10b981', 0.15)
                                : isDark 
                                  ? alpha('#8b5cf6', 0.15)
                                  : alpha('#8b5cf6', 0.15),
                              backdropFilter: 'blur(10px)',
                              border: `1px solid ${payment.type === 'Interest' 
                                ? alpha('#3b82f6', 0.2)
                                : alpha('#8b5cf6', 0.2)
                              }`,
                              color: payment.type === 'Interest' 
                                ? isDark ? '#3b82f6' : '#10b981'
                                : '#8b5cf6',
                              fontWeight: 600,
                              fontSize: '0.85rem'
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ 
                          py: 2.5,
                          px: 3
                        }}>
                          <Typography sx={{ 
                            color: isDark ? '#94a3b8' : '#718096',
                            fontWeight: 600
                          }}>
                            {formatCurrency(payment.remainingPrincipal)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ 
                          py: 2.5,
                          px: 3
                        }}>
                          <Typography sx={{ 
                            color: isDark ? '#f59e0b' : '#f59e0b',
                            fontWeight: 600
                          }}>
                            {formatCurrency(payment.interestEarned)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          justifyContent: 'center', 
                          alignItems: 'center',
                          textAlign: 'center'
                        }}>
                          <Box sx={{ 
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: isDark 
                              ? alpha('#8b5cf6', 0.1)
                              : alpha('#8b5cf6', 0.1),
                            backdropFilter: 'blur(10px)',
                            border: `1px solid ${alpha('#8b5cf6', 0.2)}`,
                            mb: 3
                          }}>
                            <ReceiptLongIcon sx={{ 
                              fontSize: 36,
                              color: '#8b5cf6',
                              opacity: 0.8
                            }} />
                          </Box>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 600,
                              color: 'text.primary',
                              mb: 1
                            }}
                          >
                            No Payments Found
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'text.secondary',
                              maxWidth: 400
                            }}
                          >
                            No payment transactions have been recorded for this girvi yet.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              
              {/* Table Footer */}
              {payments?.length > 0 && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  p: 2.5,
                  borderTop: `1px solid ${isDark 
                    ? 'rgba(51, 65, 85, 0.2)'
                    : 'rgba(226, 232, 240, 0.3)'
                  }`,
                  backgroundColor: isDark 
                    ? 'rgba(30, 41, 59, 0.2)'
                    : 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Showing {payments?.length || 0} payment records
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      disabled
                      sx={{
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        color: 'text.secondary',
                        minWidth: '32px',
                        height: '32px'
                      }}
                    >
                      ←
                    </Button>
                    <Button
                      size="small"
                      sx={{
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        backgroundColor: isDark 
                          ? alpha('#8b5cf6', 0.1)
                          : alpha('#8b5cf6', 0.1),
                        color: '#8b5cf6',
                        minWidth: '32px',
                        height: '32px'
                      }}
                    >
                      1
                    </Button>
                    <Button
                      size="small"
                      disabled
                      sx={{
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        color: 'text.secondary',
                        minWidth: '32px',
                        height: '32px'
                      }}
                    >
                      →
                    </Button>
                  </Box>
                </Box>
              )}
            </TableContainer>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default GirviLedger;